import React from 'react';
import styles from './styles.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import useFetch from 'use-http';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import { ISession } from 'types/session';
import moment from 'moment';
import { formatServiceTypeTitle, formatUserFullName } from 'utils/helpers';

interface FormInputs {
	hours: number;
}

// Функция для отображения должности
function displayPosition(session: ISession) {
	switch (true) {
		case !!session?.outsourcing?.pos?.title:
			return formatServiceTypeTitle(session?.outsourcing?.pos?.title);
		case !!session?.request?.position?.title:
			return formatServiceTypeTitle(session?.request?.position?.title);
		case !!session?.begin?.outsourcing?.position:
			return formatServiceTypeTitle(session?.begin?.outsourcing?.position);
		default:
			return 'Не указана';
	}
}

export default function EditSession() {
	const navigate = useNavigate();
	const { sessionId } = useParams();
	const [session, setSession] = React.useState<ISession | null>(null);

	const { get: fetchSession, loading: loadingGet } = useFetch(`/api/session`);
	const { patch: updateSession, loading: loadingPatch } = useFetch(`/api/session`);

	const { handleSubmit, control } = useForm<FormInputs>();

	const loadSessionData = React.useCallback(async () => {
		if (sessionId) {
			const sessionData = await fetchSession(sessionId);
			if (sessionData?.id) {
				setSession(sessionData);
			}
		}
	}, [sessionId, fetchSession]);

	React.useEffect(() => {
		loadSessionData();
	}, [loadSessionData]);

	const onSubmit: SubmitHandler<FormInputs> = async data => {
		if (sessionId) {
			await updateSession(sessionId, { hours: data.hours });
			navigate('/outsourcing');
		}
	};

	const isLoading = loadingGet || loadingPatch;

	if (!session) {
		return (
			<div className={styles['section-list']}>
				<h2>Загрузка...</h2>
			</div>
		);
	}

	return (
		<div className={styles['section-list']}>
			<h2>Редактирование длительности смены</h2>

			<div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
				<p>
					<strong>Сотрудник:</strong> {formatUserFullName(session.user)}
				</p>
				<p>
					<strong>Магазин:</strong> {session.store?.title}
				</p>
				<p>
					<strong>Тип услуги:</strong> {displayPosition(session)}
				</p>
				<p>
					<strong>Время прихода:</strong>{' '}
					{session.begin?.createdAt ? moment(session.begin.createdAt).format('DD.MM.YYYY HH:mm') : 'Не указано'}
				</p>
				<p>
					<strong>Время ухода:</strong>{' '}
					{session.end?.createdAt ? moment(session.end.createdAt).format('DD.MM.YYYY HH:mm') : 'Не указано'}
				</p>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
				<div className="row">
					<div className="column-xs-12">
						<Controller
							name="hours"
							control={control}
							defaultValue={(session as any).hours || 0}
							rules={{
								required: 'Обязательное поле',
								min: {
									value: 0,
									message: 'Минимальное значение: 0',
								},
								max: {
									value: 24,
									message: 'Максимальное значение: 24',
								},
							}}
							render={({ field, fieldState }) => (
								<Input
									type="number"
									onChange={e => field.onChange(parseInt(e.target.value) || 0)}
									label={
										<span>
											Количество часов на оплату<span style={{ color: 'red' }}>*</span>
										</span>
									}
									onBlur={field.onBlur}
									name={field.name}
									error={fieldState.error}
									defaultValue={field.value}
									min="0"
								/>
							)}
						/>
					</div>
					<div className="column-xs-12">
						<div className="row">
							<div className="column-xs-6">
								<Button
									variant="violet"
									onClick={() => {
										localStorage.setItem('outsourcing-active-tab', 'report');
										navigate('/outsourcing');
									}}
									style={{ margin: 'auto' }}
									disabled={isLoading}
								>
									Назад
								</Button>
							</div>
							<div className="column-xs-6">
								<Button type="submit" style={{ margin: 'auto' }} disabled={isLoading}>
									{isLoading ? 'Сохранение...' : 'Подтвердить'}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
}
